/**
 * API Client with Authentication Interceptors
 *
 * Provides a centralized HTTP client for making authenticated API requests.
 * Features:
 * - Automatic authentication header injection
 * - Token refresh on 401 responses
 * - Request/response interceptors
 * - Type-safe API methods
 * - Consistent error handling
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Standard API error response structure
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}

/**
 * Standard API success response structure
 */
export interface ApiResponse<T> {
  success: true;
  data: T;
}

/**
 * Standard API error response structure
 */
export interface ApiErrorResponse {
  success: false;
  error: ApiError;
}

/**
 * Union type for all API responses
 */
export type ApiResult<T> = ApiResponse<T> | ApiErrorResponse;

/**
 * Request configuration options
 */
export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Request body (will be JSON stringified if object) */
  body?: unknown;
  /** Skip authentication header */
  skipAuth?: boolean;
  /** Custom timeout in milliseconds */
  timeout?: number;
}

/**
 * Token refresh handler type
 */
export type TokenRefreshHandler = () => Promise<string | null>;

/**
 * Auth state getter type
 */
export type AuthStateGetter = () => {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: Date | null;
};

/**
 * Logout handler type
 */
export type LogoutHandler = () => void;

/**
 * Interceptor function type
 */
export type RequestInterceptor = (
  url: string,
  options: RequestInit
) => RequestInit | Promise<RequestInit>;

export type ResponseInterceptor = (
  response: Response,
  requestUrl: string,
  requestOptions: RequestInit
) => Response | Promise<Response>;

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const TOKEN_STORAGE_KEY = 'auth_token';

// =============================================================================
// API Client Class
// =============================================================================

/**
 * API Client with authentication support and interceptors
 */
export class ApiClient {
  private baseUrl: string;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private tokenRefreshHandler: TokenRefreshHandler | null = null;
  private authStateGetter: AuthStateGetter | null = null;
  private logoutHandler: LogoutHandler | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl?: string) {
    // Use provided baseUrl or default to /api
    // Note: The actual base URL should be configured via setBaseUrl or passed to constructor
    this.baseUrl = baseUrl ?? '/api';

    // Add default auth interceptor
    this.addRequestInterceptor(this.authInterceptor.bind(this));

    // Add default response interceptor for 401 handling
    this.addResponseInterceptor(this.unauthorizedInterceptor.bind(this));
  }

  // ===========================================================================
  // Configuration Methods
  // ===========================================================================

  /**
   * Set the base URL for API requests
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Set the token refresh handler
   */
  setTokenRefreshHandler(handler: TokenRefreshHandler): void {
    this.tokenRefreshHandler = handler;
  }

  /**
   * Set the auth state getter
   */
  setAuthStateGetter(getter: AuthStateGetter): void {
    this.authStateGetter = getter;
  }

  /**
   * Set the logout handler
   */
  setLogoutHandler(handler: LogoutHandler): void {
    this.logoutHandler = handler;
  }

  // ===========================================================================
  // Interceptor Methods
  // ===========================================================================

  /**
   * Add a request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): () => void {
    this.requestInterceptors.push(interceptor);
    return () => {
      const index = this.requestInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.requestInterceptors.splice(index, 1);
      }
    };
  }

  /**
   * Add a response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
    this.responseInterceptors.push(interceptor);
    return () => {
      const index = this.responseInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.responseInterceptors.splice(index, 1);
      }
    };
  }

  // ===========================================================================
  // Default Interceptors
  // ===========================================================================

  /**
   * Authentication interceptor - adds Bearer token to requests
   */
  private authInterceptor(
    _url: string,
    options: RequestInit
  ): RequestInit {
    const token = this.getAccessToken();

    if (!token) {
      return options;
    }

    const headers = new Headers(options.headers);

    // Don't override if Authorization is already set
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return {
      ...options,
      headers,
    };
  }

  /**
   * Unauthorized response interceptor - handles 401 responses
   */
  private async unauthorizedInterceptor(
    response: Response,
    requestUrl: string,
    requestOptions: RequestInit
  ): Promise<Response> {
    // Only handle 401 responses
    if (response.status !== 401) {
      return response;
    }

    // Skip if this is the refresh endpoint itself
    if (requestUrl.includes('/auth/refresh') || requestUrl.includes('/auth/login')) {
      return response;
    }

    // Try to refresh the token
    const newToken = await this.handleTokenRefresh();

    if (!newToken) {
      // Token refresh failed, trigger logout
      this.handleLogout();
      return response;
    }

    // Retry the original request with the new token
    const headers = new Headers(requestOptions.headers);
    headers.set('Authorization', `Bearer ${newToken}`);

    const retryResponse = await fetch(requestUrl, {
      ...requestOptions,
      headers,
    });

    return retryResponse;
  }

  // ===========================================================================
  // Token Management
  // ===========================================================================

  /**
   * Get the current access token
   */
  private getAccessToken(): string | null {
    // First try the auth state getter
    if (this.authStateGetter) {
      return this.authStateGetter().accessToken;
    }

    // Fall back to localStorage
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Handle token refresh with deduplication
   */
  private async handleTokenRefresh(): Promise<string | null> {
    // If already refreshing, wait for the existing refresh
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // If no refresh handler, return null
    if (!this.tokenRefreshHandler) {
      return null;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.tokenRefreshHandler();

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Handle logout
   */
  private handleLogout(): void {
    if (this.logoutHandler) {
      this.logoutHandler();
    }
  }

  // ===========================================================================
  // Request Methods
  // ===========================================================================

  /**
   * Make a request with full configuration
   */
  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResult<T>> {
    const { body, skipAuth, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;

    // Build the full URL
    const url = this.buildUrl(endpoint);

    // Prepare request options with merged headers
    const existingHeaders = fetchOptions.headers;
    const mergedHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Merge existing headers
    if (existingHeaders) {
      if (existingHeaders instanceof Headers) {
        existingHeaders.forEach((value, key) => {
          mergedHeaders[key] = value;
        });
      } else if (Array.isArray(existingHeaders)) {
        existingHeaders.forEach(([key, value]) => {
          mergedHeaders[key] = value;
        });
      } else {
        Object.assign(mergedHeaders, existingHeaders);
      }
    }

    let requestOptions: RequestInit = {
      ...fetchOptions,
      headers: mergedHeaders,
      credentials: 'include', // Include cookies for HTTP-only token support
    };

    // Add body if provided
    if (body !== undefined) {
      requestOptions.body =
        typeof body === 'string' ? body : JSON.stringify(body);
    }

    // Apply request interceptors (unless skipping auth)
    if (!skipAuth) {
      for (const interceptor of this.requestInterceptors) {
        requestOptions = await interceptor(url, requestOptions);
      }
    }

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // Make the request
      let response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Apply response interceptors
      for (const interceptor of this.responseInterceptors) {
        response = await interceptor(response, url, requestOptions);
      }

      // Parse response
      const data = await response.json();

      // Return typed response
      return data as ApiResult<T>;
    } catch (error) {
      // Handle network errors
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: {
              code: 'TIMEOUT',
              message: 'Request timed out',
            },
          };
        }
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: 'Unable to connect to the server',
        },
      };
    }
  }

  /**
   * Build the full URL for an endpoint
   */
  private buildUrl(endpoint: string): string {
    // Handle absolute URLs
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }

    // Ensure endpoint starts with /
    const normalizedEndpoint = endpoint.startsWith('/')
      ? endpoint
      : `/${endpoint}`;

    return `${this.baseUrl}${normalizedEndpoint}`;
  }

  // ===========================================================================
  // Convenience Methods
  // ===========================================================================

  /**
   * Make a GET request
   */
  async get<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * Make a PUT request
   */
  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * Make a PATCH request
   */
  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method'>
  ): Promise<ApiResult<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

/**
 * Default API client instance
 */
let defaultClient: ApiClient | null = null;

/**
 * Get the default API client instance
 */
export function getApiClient(): ApiClient {
  if (!defaultClient) {
    defaultClient = new ApiClient();
  }
  return defaultClient;
}

/**
 * Reset the default API client (useful for testing)
 */
export function resetApiClient(): void {
  defaultClient = null;
}

/**
 * Configure the default API client with auth handlers
 */
export function configureApiClient(config: {
  tokenRefreshHandler?: TokenRefreshHandler;
  authStateGetter?: AuthStateGetter;
  logoutHandler?: LogoutHandler;
}): ApiClient {
  const client = getApiClient();

  if (config.tokenRefreshHandler) {
    client.setTokenRefreshHandler(config.tokenRefreshHandler);
  }

  if (config.authStateGetter) {
    client.setAuthStateGetter(config.authStateGetter);
  }

  if (config.logoutHandler) {
    client.setLogoutHandler(config.logoutHandler);
  }

  return client;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a response is successful
 */
export function isSuccess<T>(result: ApiResult<T>): result is ApiResponse<T> {
  return result.success === true;
}

/**
 * Check if a response is an error
 */
export function isError<T>(result: ApiResult<T>): result is ApiErrorResponse {
  return result.success === false;
}

/**
 * Extract data from a successful response or throw
 */
export function unwrapResponse<T>(result: ApiResult<T>): T {
  if (isSuccess(result)) {
    return result.data;
  }
  throw new ApiClientError(result.error);
}

/**
 * Extract data from a successful response or return default value
 */
export function unwrapResponseOr<T>(result: ApiResult<T>, defaultValue: T): T {
  if (isSuccess(result)) {
    return result.data;
  }
  return defaultValue;
}

// =============================================================================
// Error Class
// =============================================================================

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  public readonly code: string;
  public readonly details?: Record<string, string[]>;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.code = error.code;
    this.details = error.details;
  }
}

// =============================================================================
// Export Default Instance
// =============================================================================

export default getApiClient();
